/* ============================================================
   PHONE AUTOMATION — PROTOTAIP DESIGN (dalam /workspace-v4 Control Panel)
   ============================================================
   ⛔ RUPA SAHAJA. Tiada WebUSB/ADB, tiada server, tiada Firestore, tiada
   simpanan. Semua `useState` tempatan; pilihan tak diingat selepas reload
   (sengaja — prototaip). Tujuan: tunjuk macam mana ciri "post ke telefon /
   Shopee" duduk DALAM Control Panel sedia ada (toggle + Post To + Cara post),
   tanpa belajar flow baru.

   Keputusan reka bentuk (user 22 Okt):
   · Toggle "Phone automation": OFF = robot extension (TikTok/FB spt sekarang),
     ON = mod telefon (post ke apps atas telefon, cth Shopee).
   · Mod telefon (Shopee) = "Post now" ATAU "Draft" SAHAJA — TIADA schedule
     (telefon perlu bersambung → jadual unattended = kawalan palsu).
   · Chip status telefon sentiasa nampak bila mod telefon ON (syarat wajib).

   UI = English (prototaip). Bila dibuka kepada pelanggan: ayat masuk i18n +
   pil EN/BM, dan baru disambung ke enjin sebenar.
   ============================================================ */

import { useState } from 'react';
import { Smartphone, Zap, FileText, LayoutGrid, Flag, Cable, CircleCheck, CircleSlash } from 'lucide-react';

// Laluan SVG jenama (dari PostToV39 — Tabler Icons, MIT).
const BRAND = {
  tiktok: 'M21 7.917v4.034a9.948 9.948 0 0 1 -5 -1.951v4.5a6.5 6.5 0 1 1 -8 -6.326v4.326a2.5 2.5 0 1 0 4 2v-11.5h4.083a6.005 6.005 0 0 0 4.917 4.917',
  facebook: 'M7 10v4h3v7h4v-7h3l1 -4h-4v-2a1 1 0 0 1 1 -1h3v-4h-3a5 5 0 0 0 -5 5v2h-3',
  threads: 'M19 7.5c-1.333 -3 -3.667 -4.5 -7 -4.5c-5 0 -8 2.5 -8 9s3.5 9 8 9s7 -3 7 -5s-1 -5 -7 -5c-2.5 0 -3 1.25 -3 2.5c0 1.5 1 2.5 2.5 2.5c2.5 0 3.5 -1.5 3.5 -5s-2 -4 -3 -4s-1.833 .333 -2.5 1',
  shopee: 'M8 9a4 4 0 1 1 8 0M5 9h14l-1 11H6zM9 13v3M15 13v3', // bag ringkas (mock)
};

function BrandIcon({ d }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

// Kad "pilih satu" — rupa SAMA PostToV39 / LaluanFbV39 (monokrom, dipilih = isian gelap).
function PickCard({ on, locked, onClick, icon, label, lockText }) {
  return (
    <button
      type="button" role="radio" aria-checked={on} disabled={locked}
      onClick={() => { if (!locked && !on) onClick(); }}
      style={{
        position: 'relative', minWidth: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
        padding: '10px 5px', font: 'inherit', textAlign: 'center', lineHeight: 1.2,
        cursor: locked ? 'not-allowed' : 'pointer', borderRadius: 'var(--s-r)',
        background: on ? 'var(--s-primary)' : 'var(--s-fill)',
        border: on ? '1px solid var(--s-primary)' : locked ? '1px dashed var(--s-line-strong)' : '1px solid var(--s-line-strong)',
        color: on ? 'var(--s-primary-ink)' : 'var(--s-ink-3)', opacity: locked ? 0.55 : 1,
      }}
      title={locked ? lockText : undefined}
    >
      {locked && (
        <span className="material-symbols-outlined" aria-hidden="true"
          style={{ position: 'absolute', top: 4, right: 5, fontSize: 13 }}>lock</span>
      )}
      {icon}
      <span style={{ fontSize: 12, fontWeight: on ? 700 : 500 }}>{label}</span>
    </button>
  );
}

const SECT = { fontSize: 12, fontWeight: 600, color: 'var(--s-ink-2)', margin: '0 0 8px' };
const GRID3 = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 };
const GRID2 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 };

export default function PhoneAutomationPanel() {
  const [phoneMode, setPhoneMode] = useState(false);
  const [extTarget, setExtTarget] = useState('facebook'); // mod extension: tiktok/facebook/threads
  const [shopeeRoute, setShopeeRoute] = useState('now');   // mod telefon: now/draft
  const [fbRoute, setFbRoute] = useState('page');          // mod extension: library/page
  const [connected, setConnected] = useState(false);       // chip telefon (mock)

  return (
    <div style={{ padding: '4px 2px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Kaunter video cloud (rupa sedia ada) */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600,
        color: 'var(--s-ok)', background: 'var(--s-ok-soft)', borderRadius: 'var(--s-r)', padding: '8px 10px',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>cloud</span>
        0 / 300 video cloud hari ni
      </div>

      {/* ── TOGGLE: Phone automation ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        border: '1px solid var(--s-line-strong)', borderRadius: 'var(--s-r)', padding: '10px 12px',
        background: phoneMode ? 'var(--s-brand-soft)' : 'transparent',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Smartphone size={17} strokeWidth={2} style={{ color: phoneMode ? 'var(--s-brand)' : 'var(--s-ink-2)', flex: '0 0 auto' }} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>Phone automation</span>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--s-ink-2)' }}>
              {phoneMode ? 'Post straight from your connected phone' : 'Off — posting via the SESAAT extension robot'}
            </span>
          </span>
        </span>
        <button type="button" role="switch" aria-checked={phoneMode} onClick={() => setPhoneMode((v) => !v)}
          style={{
            flex: '0 0 auto', width: 42, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 2,
            background: phoneMode ? 'var(--s-brand)' : 'var(--s-line-strong)', transition: 'background .15s',
            display: 'flex', justifyContent: phoneMode ? 'flex-end' : 'flex-start', alignItems: 'center',
          }}>
          <span style={{ width: 20, height: 20, borderRadius: 999, background: '#fff', display: 'block' }} />
        </button>
      </div>

      {/* ── Chip status telefon (hanya mod telefon) ── */}
      {phoneMode && (
        <button type="button" onClick={() => setConnected((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, font: 'inherit', textAlign: 'left', cursor: 'pointer',
            border: `1px solid ${connected ? 'var(--s-ok)' : 'var(--s-warn)'}`,
            background: connected ? 'var(--s-ok-soft)' : 'var(--s-warn-soft)',
            color: connected ? 'var(--s-ok)' : 'var(--s-warn)', borderRadius: 'var(--s-r)', padding: '8px 10px', fontSize: 12.5,
          }}
          title="Prototaip: klik untuk tukar keadaan (mock)">
          {connected ? <CircleCheck size={15} /> : <CircleSlash size={15} />}
          <b>Phone: {connected ? 'Connected' : 'Not connected'}</b>
          {!connected && <span style={{ color: 'var(--s-ink-2)', fontWeight: 400 }}>— plug in USB &amp; press Connect</span>}
          <Cable size={14} style={{ marginLeft: 'auto', opacity: .6 }} />
        </button>
      )}

      {/* ── Post To ── */}
      <div>
        <p style={SECT}>Post To</p>
        {!phoneMode ? (
          <div style={GRID3}>
            <PickCard on={extTarget === 'tiktok'} onClick={() => setExtTarget('tiktok')} icon={<BrandIcon d={BRAND.tiktok} />} label="TikTok" />
            <PickCard on={extTarget === 'facebook'} onClick={() => setExtTarget('facebook')} icon={<BrandIcon d={BRAND.facebook} />} label="Facebook Reels" />
            <PickCard on={extTarget === 'threads'} locked lockText="Coming soon" icon={<BrandIcon d={BRAND.threads} />} label="Threads" />
          </div>
        ) : (
          <div style={GRID3}>
            <PickCard on icon={<BrandIcon d={BRAND.shopee} />} label="Shopee Video" />
            <PickCard locked lockText="Configure an app first" icon={<LayoutGrid size={20} />} label="More apps" />
            <PickCard locked lockText="Configure an app first" icon={<LayoutGrid size={20} />} label="Soon" />
          </div>
        )}
        {phoneMode && (
          <p style={{ fontSize: 11, color: 'var(--s-ink-2)', margin: '8px 0 0' }}>
            Posting runs on the app installed on your phone. Shopee is ready; more apps can be configured later.
          </p>
        )}
      </div>

      {/* ── Cara post / Posting route (ubah ikut mod) ── */}
      <div>
        <p style={SECT}>Cara post</p>
        {!phoneMode ? (
          <>
            <div style={GRID2}>
              <PickCard on={fbRoute === 'library'} onClick={() => setFbRoute('library')} icon={<LayoutGrid size={20} />} label="Content Library" />
              <PickCard on={fbRoute === 'page'} onClick={() => setFbRoute('page')} icon={<Flag size={20} />} label="Page" />
            </div>
            <p style={{ fontSize: 11, color: 'var(--s-ink-2)', margin: '8px 0 0' }}>
              Choose the route, then schedule on the calendar or post now (extension robot).
            </p>
          </>
        ) : (
          <>
            <div style={GRID2}>
              <PickCard on={shopeeRoute === 'now'} onClick={() => setShopeeRoute('now')} icon={<Zap size={20} />} label="Post now" />
              <PickCard on={shopeeRoute === 'draft'} onClick={() => setShopeeRoute('draft')} icon={<FileText size={20} />} label="Save as Draft" />
            </div>
            <p style={{ fontSize: 11, color: 'var(--s-ink-2)', margin: '8px 0 0', lineHeight: 1.5 }}>
              {shopeeRoute === 'now'
                ? 'Guided session — we fill the video, caption & product, then YOU tap Publish. Review is required.'
                : 'We prepare everything (video + caption + product) and leave it as a Shopee draft on your phone — you publish later.'}
              <br /><b>No scheduling for Shopee</b> — the phone must be connected, so there is no calendar slot here.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
